import { Grid, Typography } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import type { Dispatch, SetStateAction } from 'react';
import { maxTime } from 'utils/roundingRules';

type TranslationFn = (key: string) => string;
type ErrorsMap = Record<string, string | undefined>;

type EntryFormData = {
    EntryEarlyChangePoint: Dayjs;
    EntryEarlyExternalRounding: Dayjs;
    EntryEarlyInternalRounding: Dayjs;
    EntryEarlyExternalGracePeriod: Dayjs;
    EntryEarlyInternalGracePeriod: Dayjs;
    EntryLateChangePoint: Dayjs;
    EntryLateExternalRounding: Dayjs;
    EntryLateInternalRounding: Dayjs;
    EntryLateExternalGracePeriod: Dayjs;
    EntryLateInternalGracePeriod: Dayjs;
};

interface EntryMarkingProps {
    t: TranslationFn;
    loadingGettingRounding?: boolean;
    formData: EntryFormData;
    updateFormData: (field: keyof EntryFormData, value: Dayjs) => void;
    errors: ErrorsMap;
    setErrors: Dispatch<SetStateAction<ErrorsMap>>;
}

const EntryMarking = ({ t, loadingGettingRounding = false, formData, updateFormData, errors, setErrors }: EntryMarkingProps) => {
    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <Typography variant="body2">
                        <b>{t('settings.rounding_rule.table.modals.add.tabs.in_punch.early_punch_rounding')}</b>
                    </Typography>

                    <br />
                    <br />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.change_point')}
                            value={formData.EntryEarlyChangePoint}
                            onChange={(nv) => nv && updateFormData('EntryEarlyChangePoint', nv)}
                            ampm={false}
                            sx={{ mb: 2.5 }}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={loadingGettingRounding}
                        />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.external_rounding')}
                                    value={formData.EntryEarlyExternalRounding}
                                    onChange={(nv) => nv && updateFormData('EntryEarlyExternalRounding', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    slotProps={{ textField: { fullWidth: true } }}
                                    disabled={loadingGettingRounding}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    disabled={dayjs(new Date().setHours(0, 0, 0, 0)).isSame(formData.EntryEarlyChangePoint) || loadingGettingRounding}
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.internal_rounding')}
                                    value={formData.EntryEarlyInternalRounding}
                                    onChange={(nv) => nv && updateFormData('EntryEarlyInternalRounding', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.external_grace_period')}
                                    value={formData.EntryEarlyExternalGracePeriod}
                                    onChange={(nv) => nv && updateFormData('EntryEarlyExternalGracePeriod', nv)}
                                    ampm={false}
                                    maxTime={maxTime(formData.EntryEarlyExternalRounding)}
                                    minutesStep={1}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    onError={(error) => setErrors({ ...errors, EntryEarlyExternalGracePeriod: error ?? '' })}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: errors['EntryEarlyExternalGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                        },
                                    }}
                                    disabled={loadingGettingRounding}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    disabled={dayjs(new Date().setHours(0, 0, 0, 0)).isSame(formData.EntryEarlyChangePoint) || loadingGettingRounding}
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.internal_grace_period')}
                                    value={formData.EntryEarlyInternalGracePeriod}
                                    onChange={(nv) => nv && updateFormData('EntryEarlyInternalGracePeriod', nv)}
                                    ampm={false}
                                    maxTime={maxTime(formData.EntryEarlyInternalRounding)}
                                    minutesStep={1}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    onError={(error) => setErrors({ ...errors, EntryEarlyInternalGracePeriod: error ?? '' })}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: errors['EntryEarlyInternalGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <Typography variant="body2">
                        <b>{t('settings.rounding_rule.table.modals.add.tabs.in_punch.late_punch_rounding')}</b>
                    </Typography>

                    <br />
                    <br />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.change_point')}
                            value={formData.EntryLateChangePoint}
                            onChange={(nv) => nv && updateFormData('EntryLateChangePoint', nv)}
                            ampm={false}
                            sx={{ mb: 2.5 }}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={loadingGettingRounding}
                        />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    disabled={dayjs(new Date().setHours(0, 0, 0, 0)).isSame(formData.EntryLateChangePoint) || loadingGettingRounding}
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.internal_rounding')}
                                    value={formData.EntryLateInternalRounding}
                                    onChange={(nv) => nv && updateFormData('EntryLateInternalRounding', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.external_rounding')}
                                    value={formData.EntryLateExternalRounding}
                                    onChange={(nv) => nv && updateFormData('EntryLateExternalRounding', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    slotProps={{ textField: { fullWidth: true } }}
                                    disabled={loadingGettingRounding}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    disabled={dayjs(new Date().setHours(0, 0, 0, 0)).isSame(formData.EntryLateChangePoint) || loadingGettingRounding}
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.internal_grace_period')}
                                    value={formData.EntryLateInternalGracePeriod}
                                    onChange={(nv) => nv && updateFormData('EntryLateInternalGracePeriod', nv)}
                                    ampm={false}
                                    maxTime={maxTime(formData.EntryLateInternalRounding)}
                                    minutesStep={1}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    onError={(error) => setErrors({ ...errors, EntryLateInternalGracePeriod: error ?? '' })}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: errors['EntryLateInternalGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.external_grace_period')}
                                    value={formData.EntryLateExternalGracePeriod}
                                    onChange={(nv) => nv && updateFormData('EntryLateExternalGracePeriod', nv)}
                                    ampm={false}
                                    maxTime={maxTime(formData.EntryLateExternalRounding)}
                                    minutesStep={1}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    onError={(error) => setErrors({ ...errors, EntryLateExternalGracePeriod: error ?? '' })}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: errors['EntryLateExternalGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                        },
                                    }}
                                    disabled={loadingGettingRounding}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </Grid>
            </Grid>
        </>
    );
};

export default EntryMarking;
